import * as React from 'react'
import * as formik from 'formik'
import { styled } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import * as service from '@owl-nest/services'
import * as hooks from '@owl-nest/hooks'
import { t } from '@owl-nest/localize'

import { useStep, getStepIndex, getStepProgress, Step } from '../useStep'
import { Progress } from '../components/Progress'
import { Coaching } from '../components/Coaching'
import { Tip } from '../components/Tip'
import { Buttons } from '../components/Buttons'
import { Actions } from '../components/Actions'

import { SolidContent } from './styles'

type CategoryProps = {
  project: model.project.ProjectForOwner
}

export function Category({ project }: CategoryProps): React.ReactElement<any> {
  const { response } = service.category.useCategories({ fetchOnMount: true })
  const { setStepValue } = useStep(project)

  const projectIsDonationBased = model.project.isDonationBased(project)

  const categories = React.useMemo(() => {
    if (response.status === hooks.QueryStatus.SUCCESS) {
      const categories = response.data.categories
        .filter((category) => category.slug !== 'other' && category.slug !== 'fundraisers')
        .sort((a, b) => {
          const nameA = model.i18n.get(a.name)
          const nameB = model.i18n.get(b.name)
          if (nameA === undefined && nameB === undefined) {
            return 0
          }
          if (nameA === undefined) {
            return 1
          }
          if (nameB === undefined) {
            return -1
          }
          return nameA.localeCompare(nameB)
        })

      // Add `fundraisers` category at the beginning of the list for donation based projects
      const fundraisers = response.data.categories.find((category) => category.slug === 'fundraisers')
      if (fundraisers !== undefined) {
        if (projectIsDonationBased) {
          categories.unshift(fundraisers)
        } else {
          categories.push(fundraisers)
        }
      }

      const other = response.data.categories.find((category) => category.slug === 'other')
      if (other !== undefined) {
        categories.push(other)
      }
      return categories
    }
  }, [response.status])

  return (
    <>
      <Progress
        progress={getStepProgress(Step.CATEGORY)}
        step={getStepIndex(Step.CATEGORY)}
        name={t('Project’s main category')}
      />
      <Coaching project={project}>{t('Which category best suits your project?')}</Coaching>

      <formik.Formik
        initialValues={{
          category: projectIsDonationBased ? 'fundraisers' : undefined,
        }}
        onSubmit={(values) => {
          setStepValue({ main_tag: values.category })
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formikBag) => {
          return (
            <Form onSubmit={formikBag.handleSubmit}>
              <SolidContent>
                <formik.Field name="category">
                  {({ field, form }: formik.FieldProps<string>) => {
                    return (
                      <List>
                        {categories &&
                          categories.map((category) => (
                            <li key={category.id}>
                              <plume.ClickableTag
                                isActive={field.value === category.slug}
                                label={model.i18n.get(category.name) || ''}
                                onClick={() => {
                                  form.setFieldValue('category', category.slug)
                                }}
                                type="button"
                              />
                            </li>
                          ))}
                      </List>
                    )
                  }}
                </formik.Field>
              </SolidContent>

              <Actions>
                <Tip>{t('Nothing final, you can edit this information later.')}</Tip>
                <Buttons>
                  <plume.LinkAsButton kind="secondary" type="button" onClick={() => setStepValue()}>
                    {t('Skip')}
                  </plume.LinkAsButton>
                  <plume.Button kind="primary">{t('Next')}</plume.Button>
                </Buttons>
              </Actions>
            </Form>
          )
        }}
      </formik.Formik>
    </>
  )
}

const Form = styled.form`
  height: calc(100% - (132px + 2 * 22px));

  li {
    padding-left: 4px;
    padding-right: 4px;
  }
`

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  list-style: none;
  margin: 0 auto;
  max-height: 45vh;
  max-width: 445px;
  overflow: scroll;
  padding: 0;

  ${plume.ClickableTag} {
    margin-bottom: 16px;
    margin-right: 8px;
  }
`
